import EmailIcon from '@mui/icons-material/Email';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';

import { Application } from '@features/applications';
import ScheduleMessageModal from '@features/messaging/components/ScheduleMessageModal/ScheduleMessageModal';
import { useUnreadMessages } from '@features/messaging/hooks/useUnreadMessages';
import { useSenEnterpriseMessage } from '@features/messaging/hooks/useSenEnterpriseMessage';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import AssistantIcon  from '@mui/icons-material/Assistant';
import { AITooltipText } from '@features/messaging/constants';
import { HelpOutline } from '@mui/icons-material';

type EnterpriseSendMessageButtonsProps = {
  setErrorText: Dispatch<SetStateAction<string>>;
  setShowAiPromptModal?: Dispatch<SetStateAction<boolean>>;
  message: string;
  disabledUnreadButton?: boolean;
  threadId?: number;
  companyId?: number;
  showMarkReadButton?: boolean;
  applicantId?: number;
  recipientId: number | undefined;
  application?: Application;
  onSendSuccess: () => void;
  file: File | null;
  onUnreadSuccess: () => void;
};

const EnterpriseSendMessageButtons: FC<EnterpriseSendMessageButtonsProps> = ({
  setErrorText,
  setShowAiPromptModal,
  message,
  application,
  disabledUnreadButton,
  showMarkReadButton,
  recipientId,
  threadId,
  applicantId,
  companyId,
  onSendSuccess,
  file,
  onUnreadSuccess,
}) => {
  const [isSendMenuOpen, setIsSendMenuOpen] = useState(false);
  const [isScheduleMessageModalOpen, setIsScheduleMessageModalOpen] =
    useState(false);
  const sendButtonRef = useRef<HTMLDivElement>(null);
  const { sendMessage, isSendingMessage } = useSenEnterpriseMessage({
    setErrorText,
  });
  const handleSend = async (message: string) => {
    const payload = {
      applicant_id: applicantId,
      company_id: companyId ? companyId.toString() : undefined,
      message,
      message_doc: file,
      application_id: application?.id,
      ...(threadId ? { thread_id: threadId } : {}),
    };

    sendMessage(payload, {
      onSuccess: () => onSendSuccess(),
    });

    // setProgress(100);
  };

  const { unreadMessages } = useUnreadMessages({ setErrorText });

  return (
    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
      <ButtonGroup
        variant="contained"
        color="primary"
        ref={sendButtonRef}
        disabled={!message || isSendingMessage}
        sx={{ flexGrow: 1, borderRadius: '6px' }}
      >
        <LoadingButton
          loading={isSendingMessage}
          sx={{ borderRadius: '6px', fontSize: '14px' }}
          fullWidth
          onClick={() =>
            handleSend(message)
          }
        >
          Send Message
        </LoadingButton>
        {/*
      // TODO: finish schedule message logic when backend is ready
      <Button size="small" onClick={() => setIsSendMenuOpen(true)}>
          <ArrowDropDownIcon />
        </Button> */}
      </ButtonGroup>
      
      {/* {showMarkReadButton && threadId && ( */}
      {threadId && (
        <Button
          color="secondary"
          variant="outlined"
          startIcon={<EmailIcon />}
          disabled={disabledUnreadButton && !showMarkReadButton}
          onClick={() =>
            unreadMessages(
              { thread_id: threadId, is_enterprise: 1, company_id: companyId },
              {
                onSuccess: (data) => {
                  if (data.status === 200) {
                    onUnreadSuccess()
                    toast.success(data.message)
                  } else if (data.status === 400) {
                    toast.error(data.message)
                  }

                }
              },
            )
          }
        >
          Mark Unread
        </Button>
      )}
      {setShowAiPromptModal && (
        <Stack spacing={2} direction="row" alignItems="center">
          <Button
            variant="contained"
            onClick={() => setShowAiPromptModal(true)}
            startIcon={<AssistantIcon />}
          >
            Write with AI
          </Button>
          <Tooltip title={AITooltipText} placement="top" arrow>
            <HelpOutline color="secondary" fontSize="small" />
          </Tooltip>
        </Stack>
      )}
      <Popper
        open={isSendMenuOpen}
        anchorEl={sendButtonRef.current}
        disablePortal
        placement="top-end"
      >
        <Paper>
          <ClickAwayListener onClickAway={() => setIsSendMenuOpen(false)}>
            <MenuList>
              <MenuItem onClick={() => setIsScheduleMessageModalOpen(true)}>
                Schedule Message
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
      <ScheduleMessageModal
        isOpen={isScheduleMessageModalOpen}
        handleClose={() => setIsScheduleMessageModalOpen(false)}
      />
    </Stack>
  );
};

export default EnterpriseSendMessageButtons;
